import React, { useState, useEffect } from 'react';
import './App.css';
import ChatInterface from './components/ChatInterface';
import VideoRecommendations from './components/VideoRecommendations';
import { sendMessage } from './services/api';

function App() {
  const [messages, setMessages] = useState(() => {
    // Load messages from localStorage on initial render
    const saved = localStorage.getItem('bjj-chat-messages');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Convert timestamp strings back to Date objects
      return parsed.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    }
    return [];
  });
  const [recommendations, setRecommendations] = useState([]);
  const [conversationId, setConversationId] = useState(() => {
    return localStorage.getItem('bjj-conversation-id') || null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('bjj-chat-messages', JSON.stringify(messages));
  }, [messages]);

  // Save conversation ID
  useEffect(() => {
    if (conversationId) {
      localStorage.setItem('bjj-conversation-id', conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    // Load initial stats
    fetch('/api/admin/stats')
      .then(res => res.json())
      .then(data => setStats(data))
      .catch(err => console.error('Error loading stats:', err));
  }, []);

  const handleClearChat = () => {
    setMessages([]);
    setRecommendations([]);
    setConversationId(null);
    localStorage.removeItem('bjj-chat-messages');
    localStorage.removeItem('bjj-conversation-id');
  };

  const handleSendMessage = async (userMessage) => {
    // Add user message to chat
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      // Send message to API
      const response = await sendMessage(userMessage, conversationId);
      
      // Update conversation ID
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }

      // Add bot response to chat
      const botMessage = {
        role: 'assistant',
        content: response.answer,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);

      // Update recommendations
      if (response.recommendations && response.recommendations.length > 0) {
        setRecommendations(response.recommendations);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-logo-title">
            <img src="/logo.avif" alt="BJJ Logo" className="header-logo" />
            <h1>BJJ AI Instructor</h1>
          </div>
          <p className="subtitle">Ask me anything about Brazilian Jiu-Jitsu techniques</p>
          {stats && (
            <div className="stats">
              <span>{stats.total_videos} videos</span>
              <span>•</span>
              <span>{stats.total_duration_minutes} minutes of content</span>
              <span>•</span>
              <span>{stats.total_chunks} knowledge chunks</span>
            </div>
          )}
          {messages.length > 0 && (
            <button className="clear-chat-btn" onClick={handleClearChat}>
              🗑️ Clear Chat
            </button>
          )}
        </div>
      </header>

      <main className="App-main">
        <div className="chat-container">
          <ChatInterface
            messages={messages}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
          />
        </div>

        {recommendations.length > 0 && (
          <div className="recommendations-container">
            <VideoRecommendations recommendations={recommendations} />
          </div>
        )}
      </main>

      <footer className="App-footer">
        <p>Powered by AI • Content from BJJFanatics.com</p>
      </footer>
    </div>
  );
}

export default App;
